import { useEffect, useMemo, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  fetchQuickOrderCategories,
  createQuickOrderCategory,
  updateQuickOrderCategory,
  updateQuickOrderCategoryStatus,
  deleteQuickOrderCategory,
  fetchQuickOrderProducts,
  createQuickOrderProduct,
  updateQuickOrderProduct,
  updateQuickOrderProductStatus,
  deleteQuickOrderProduct,
} from '../services/quickOrderSuggestionService';
import { Edit, Trash2, Plus, ToggleLeft, ToggleRight } from 'lucide-react';

const defaultCategoryForm = {
  name: '',
  description: '',
  displayOrder: '',
  status: '1',
};

const defaultProductForm = {
  name: '',
  description: '',
  defaultPrice: '',
  displayOrder: '',
  status: '1',
};

export default function QuickOrderSuggestions() {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryForm, setCategoryForm] = useState(defaultCategoryForm);
  const [categoryEditingId, setCategoryEditingId] = useState(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productForm, setProductForm] = useState(defaultProductForm);
  const [productEditingId, setProductEditingId] = useState(null);

  const selectedCategory = useMemo(
    () => categories.find((cat) => cat.id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await fetchQuickOrderCategories({
        limit: 200,
        sortBy: 'displayOrder',
        sortOrder: 'ASC',
      });
      const records = res.data?.data?.records || res.data?.data || [];
      setCategories(records);
      if (!selectedCategoryId && records.length > 0) {
        setSelectedCategoryId(records[0].id);
      }
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadProducts = async (categoryId) => {
    if (!categoryId) {
      setProducts([]);
      return;
    }
    try {
      setLoadingProducts(true);
      const res = await fetchQuickOrderProducts({
        categoryId,
        limit: 200,
        sortBy: 'displayOrder',
        sortOrder: 'ASC',
      });
      const records = res.data?.data?.records || res.data?.data || [];
      setProducts(records);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProducts(selectedCategoryId);
  }, [selectedCategoryId]);

  const resetCategoryForm = () => {
    setCategoryEditingId(null);
    setCategoryForm(defaultCategoryForm);
  };

  const resetProductForm = () => {
    setProductEditingId(null);
    setProductForm(defaultProductForm);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast.error('Category name is required');
      return;
    }
    const payload = {
      name: categoryForm.name,
      description: categoryForm.description || null,
      displayOrder:
        categoryForm.displayOrder === ''
          ? 0
          : Number(categoryForm.displayOrder) || 0,
      status: Number(categoryForm.status) || 1,
    };
    try {
      if (categoryEditingId) {
        await updateQuickOrderCategory(categoryEditingId, payload);
        toast.success('Category updated');
      } else {
        const res = await createQuickOrderCategory(payload);
        const created = res.data?.data || res.data;
        if (created?.id) {
          setSelectedCategoryId(created.id);
        }
        toast.success('Category created');
      }
      resetCategoryForm();
      await loadCategories();
    } catch (err) {
      toast.error('Failed to save category');
    }
  };

  const confirmCategoryStatusChange = async (category) => {
    const newStatus = category.status === 1 ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this category?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateQuickOrderCategoryStatus(category.id, newStatus);
      toast.success('Status updated');
      await loadCategories();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const confirmCategoryDelete = async (category) => {
    const result = await Swal.fire({
      title: 'Delete this category?',
      text: 'Products under this category will also need manual review.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton: 'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteQuickOrderCategory(category.id);
      toast.success('Category deleted');
      if (category.id === selectedCategoryId) {
        setSelectedCategoryId(null);
      }
      await loadCategories();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const categoryId = productEditingId
      ? productForm.categoryId
      : selectedCategoryId;

    if (!categoryId) {
      toast.error('Select a category first');
      return;
    }
    if (!productForm.name) {
      toast.error('Product name is required');
      return;
    }

    const payload = {
      name: productForm.name,
      description: productForm.description || null,
      categoryId,
      defaultPrice:
        productForm.defaultPrice === ''
          ? null
          : Number(productForm.defaultPrice),
      displayOrder:
        productForm.displayOrder === ''
          ? 0
          : Number(productForm.displayOrder) || 0,
      status: Number(productForm.status) || 1,
    };

    try {
      if (productEditingId) {
        await updateQuickOrderProduct(productEditingId, payload);
        toast.success('Product updated');
      } else {
        await createQuickOrderProduct(payload);
        toast.success('Product created');
      }
      resetProductForm();
      await loadProducts(categoryId);
      await loadCategories();
    } catch (err) {
      toast.error('Failed to save product');
    }
  };

  const confirmProductStatusChange = async (product) => {
    const newStatus = product.status === 1 ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this product?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateQuickOrderProductStatus(product.id, newStatus);
      toast.success('Status updated');
      await loadProducts(product.categoryId);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const confirmProductDelete = async (product) => {
    const result = await Swal.fire({
      title: 'Delete this product?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton: 'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteQuickOrderProduct(product.id);
      toast.success('Product deleted');
      await loadProducts(product.categoryId);
      await loadCategories();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Quick Order Suggestions</h1>
          <div className="text-sm text-gray-500 dark:text-gray-300">
            Manage default categories and products that appear while creating orders.
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 space-y-4">
            <div className="border rounded-lg p-4 bg-white dark:bg-gray-800 dark:text-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Categories</h2>
                <button
                  type="button"
                  onClick={resetCategoryForm}
                  className="flex items-center text-sm text-indigo-600 hover:underline"
                >
                  <Plus size={16} className="mr-1" /> New
                </button>
              </div>
              <form onSubmit={handleCategorySubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Display Order</label>
                    <input
                      type="number"
                      value={categoryForm.displayOrder}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          displayOrder: e.target.value,
                        })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      value={categoryForm.status}
                      onChange={(e) =>
                        setCategoryForm({ ...categoryForm, status: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="1">Active</option>
                      <option value="2">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  {categoryEditingId && (
                    <button
                      type="button"
                      onClick={resetCategoryForm}
                      className="px-3 py-2 text-sm bg-gray-200 rounded dark:bg-gray-700"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded"
                  >
                    {categoryEditingId ? 'Update Category' : 'Add Category'}
                  </button>
                </div>
              </form>
            </div>
            <div className="border rounded-lg bg-white dark:bg-gray-800 dark:text-white">
              <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Category List</h2>
                {loadingCategories && <span className="text-xs text-gray-500">Loading...</span>}
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Order</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-center text-gray-500">
                          No categories found
                        </td>
                      </tr>
                    )}
                    {categories.map((cat) => (
                      <tr
                        key={cat.id}
                        className={`border-t dark:border-gray-700 cursor-pointer ${
                          selectedCategoryId === cat.id
                            ? 'bg-indigo-50 dark:bg-gray-700'
                            : ''
                        }`}
                        onClick={() => setSelectedCategoryId(cat.id)}
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium">{cat.name}</div>
                          {cat.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-300">
                              {cat.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">{cat.displayOrder}</td>
                        <td className="px-4 py-2">
                          {cat.status === 1 ? (
                            <span className="text-green-600">Active</span>
                          ) : (
                            <span className="text-gray-500">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCategoryEditingId(cat.id);
                                setCategoryForm({
                                  name: cat.name,
                                  description: cat.description || '',
                                  displayOrder: cat.displayOrder?.toString() || '',
                                  status: String(cat.status ?? 1),
                                });
                              }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmCategoryStatusChange(cat);
                              }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                              title="Toggle status"
                            >
                              {cat.status === 1 ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmCategoryDelete(cat);
                              }}
                              className="p-1 rounded hover:bg-gray-200 text-red-600 dark:hover:bg-gray-600"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="xl:col-span-2 space-y-4">
            <div className="border rounded-lg p-4 bg-white dark:bg-gray-800 dark:text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Products</h2>
                  {selectedCategory ? (
                    <p className="text-sm text-gray-500 dark:text-gray-300">
                      Managing suggestions under <strong>{selectedCategory.name}</strong>
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-300">
                      Select or create a category to add products.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setProductEditingId(null);
                    setProductForm(defaultProductForm);
                    if (selectedCategoryId) {
                      setProductForm((prev) => ({ ...prev, status: '1' }));
                    }
                  }}
                  className="flex items-center text-sm text-indigo-600 hover:underline"
                >
                  <Plus size={16} className="mr-1" /> New
                </button>
              </div>
              <form onSubmit={handleProductSubmit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Product Name</label>
                    <input
                      type="text"
                      value={productForm.name}
                      onChange={(e) =>
                        setProductForm({ ...productForm, name: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Default Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={productForm.defaultPrice}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          defaultPrice: e.target.value,
                        })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Display Order</label>
                    <input
                      type="number"
                      value={productForm.displayOrder}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          displayOrder: e.target.value,
                        })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      value={productForm.status}
                      onChange={(e) =>
                        setProductForm({ ...productForm, status: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="1">Active</option>
                      <option value="2">Inactive</option>
                    </select>
                  </div>
                  {productEditingId && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <select
                        value={productForm.categoryId || selectedCategoryId || ''}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            categoryId: e.target.value ? Number(e.target.value) : '',
                          })
                        }
                        className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="">Select</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  {productEditingId && (
                    <button
                      type="button"
                      onClick={resetProductForm}
                      className="px-3 py-2 text-sm bg-gray-200 rounded dark:bg-gray-700"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded"
                    disabled={!selectedCategoryId && !productEditingId}
                  >
                    {productEditingId ? 'Update Product' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
            <div className="border rounded-lg bg-white dark:bg-gray-800 dark:text-white">
              <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Product List</h2>
                {loadingProducts && <span className="text-xs text-gray-500">Loading...</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Price</th>
                      <th className="text-left px-4 py-2">Order</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-center text-gray-500">
                          {selectedCategoryId
                            ? 'No products found for this category'
                            : 'Select a category to view products'}
                        </td>
                      </tr>
                    )}
                    {products.map((prod) => (
                      <tr key={prod.id} className="border-t dark:border-gray-700">
                        <td className="px-4 py-2">
                          <div className="font-medium">{prod.name}</div>
                          {prod.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-300">
                              {prod.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {prod.defaultPrice != null ? Number(prod.defaultPrice).toFixed(2) : '-'}
                        </td>
                        <td className="px-4 py-2">{prod.displayOrder}</td>
                        <td className="px-4 py-2">
                          {prod.status === 1 ? (
                            <span className="text-green-600">Active</span>
                          ) : (
                            <span className="text-gray-500">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => {
                                setProductEditingId(prod.id);
                                setProductForm({
                                  name: prod.name,
                                  description: prod.description || '',
                                  defaultPrice:
                                    prod.defaultPrice != null
                                      ? prod.defaultPrice.toString()
                                      : '',
                                  displayOrder: prod.displayOrder?.toString() || '',
                                  status: String(prod.status ?? 1),
                                  categoryId: prod.categoryId,
                                });
                              }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmProductStatusChange(prod)}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                              title="Toggle status"
                            >
                              {prod.status === 1 ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmProductDelete(prod)}
                              className="p-1 rounded hover:bg-gray-200 text-red-600 dark:hover:bg-gray-600"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutWithSidebar>
  );
}
